import { Input } from "@components/forms/input";
import { IRaffleResultConfigDTO } from "@hooks/dtos/raffles/IRaffleDefaultDrawScheduleDTO";
import { Masks } from "@hooks/functions/Masks/Masks";
import { getRaffleDefaultDrawLotteryReason } from "@hooks/functions/Raffle/status";
import dayjs from "dayjs";
import { IconType } from "react-icons";
import {
  FaCalendarCheck,
  FaChartPie,
  FaCheckCircle,
  FaClock,
  FaDice,
  FaExclamationTriangle,
  FaInfoCircle,
  FaTrophy,
} from "react-icons/fa";
import { FaLandmark, FaTicket } from "react-icons/fa6";

type ScheduleMethod = "lottery" | "random";

interface IProps {
  campaign?: string | null;
  raffleKey?: string | null;
  soldPercent: number;
  soldTickets: number;
  totalTickets?: number;
  config?: IRaffleResultConfigDTO;
  isLoadingConfig: boolean;
  lotteryContest: string;
  lotteryCombination: string;
  randomDate: string;
  minRandomDate: dayjs.Dayjs;
  nextLotteryContest: number;
  maxLotteryContest: number;
  canScheduleBySoldPercent: boolean;
  lotterySourceIsTrusted: boolean;
  lotteryContestIsValid: boolean;
  canScheduleLottery: boolean;
  canScheduleRandom: boolean;
  randomDateIsValid: boolean;
  randomMinuteIsValid: boolean;
  activeMethod: ScheduleMethod;
  lotteryMethodIsCompatible: boolean;
  randomMethodIsCompatible: boolean;
  onMethodChange: (method: ScheduleMethod) => void;
  onLotteryContestChange: (value: string) => void;
  onLotteryCombinationChange: (value: string) => void;
  onRandomDateChange: (value: string) => void;
}

const masks = new Masks();

function getDateTimeLocalValue(date: dayjs.Dayjs) {
  return date.format("YYYY-MM-DDTHH:mm");
}

function getLotteryLastDrawDateText(date?: string | null) {
  if (!date || !dayjs(date).isValid()) {
    return "data não informada";
  }

  return dayjs(date).format("DD/MM/YYYY");
}

function StatusPill({
  can,
  text,
  title,
}: {
  can: boolean;
  text: string;
  title: string;
}) {
  return (
    <span
      className={`badge bg-${can ? "success" : "warning"} text-${
        can ? "white" : "dark"
      } px-3 py-2`}
      title={title}
    >
      {can ? (
        <FaCheckCircle className="me-1" />
      ) : (
        <FaExclamationTriangle className="me-1" />
      )}
      {text}
    </span>
  );
}

function RuleRow({
  can,
  icon: Icon,
  label,
  value,
  title,
}: {
  can: boolean;
  icon: IconType;
  label: string;
  value: string;
  title: string;
}) {
  return (
    <div className="border rounded-3 p-3 mb-2 bg-light bg-opacity-50">
      <div className="d-flex justify-content-between align-items-center gap-3">
        <div className="d-flex align-items-center gap-3">
          <Icon className={`fs-4 text-${can ? "success" : "warning"}`} />
          <div>
            <div className="fw-semibold">{label}</div>
            <small className="text-muted">{value}</small>
          </div>
        </div>
        <StatusPill
          can={can}
          text={can ? "Liberado" : "Pendente"}
          title={title}
        />
      </div>
    </div>
  );
}

function MethodTab({
  active,
  icon: Icon,
  title,
  description,
  color,
  isCompatible,
  onClick,
}: {
  active: boolean;
  icon: IconType;
  title: string;
  description: string;
  color: "primary" | "success";
  isCompatible: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      className={
        "btn text-start w-100 h-100 border rounded-3 p-3 bg-white " +
        (active ? "border-" + color + " border-2 shadow-sm" : "border-light") +
        (isCompatible ? "" : " opacity-50")
      }
      onClick={onClick}
      aria-pressed={active}
      aria-disabled={!isCompatible}
      title={
        isCompatible
          ? undefined
          : "Este método é incompatível com o tipo de sorteio definido para a campanha."
      }
    >
      <div className="d-flex align-items-center gap-3">
        <Icon className={`fs-2 text-${color}`} />
        <div>
          <div className="fw-bold fs-5">{title}</div>
          <div className="text-muted small">{description}</div>
        </div>
      </div>
    </button>
  );
}

function MethodInfo({ method }: { method: ScheduleMethod }) {
  const isLottery = method === "lottery";
  const description = isLottery
    ? "A Loteria Federal utiliza um resultado oficial externo como referência para o sorteio, oferecendo um critério público e verificável."
    : "O Sorteio Aleatório permite definir uma data e hora para o sorteio. A plataforma realiza o processo automaticamente entre os tickets comprados da campanha.";
  const details = isLottery
    ? [
        "Baseado em resultado oficial",
        "Critério público e auditável",
        "Ideal para campanhas com referência externa",
        "Depende da disponibilidade dos concursos",
      ]
    : [
        "Sorteio transparente e automático",
        "Utiliza apenas tickets comprados",
        "Ideal para campanhas próprias",
        "Respeita todas as validações definidas",
      ];

  return (
    <aside className="border border-primary rounded-3 p-4 h-100 bg-white">
      <div className="d-flex align-items-center gap-3 mb-3">
        <FaInfoCircle className="fs-3 text-primary" />
        <h6 className="fw-bold fs-5 mb-0">Sobre este método</h6>
      </div>
      <p className="text-muted mb-4">{description}</p>
      <hr />
      <ul className="list-unstyled mb-4">
        {details.map((detail) => (
          <li className="d-flex align-items-center gap-2 mb-2" key={detail}>
            <FaCheckCircle className="text-success" />
            <span className="text-muted">{detail}</span>
          </li>
        ))}
      </ul>
      <div className="rounded-3 p-3 bg-light border">
        <div className="d-flex align-items-center gap-2 text-primary fw-bold mb-2">
          <FaInfoCircle />
          Importante
        </div>
        <small className="text-muted">
          {isLottery
            ? "O agendamento só será concluído se a fonte da Loteria Federal estiver disponível e todas as validações forem atendidas."
            : "O agendamento só será concluído se todas as validações necessárias forem atendidas."}
        </small>
      </div>
    </aside>
  );
}

export function ModalRaffleOwnerScheduleForm({
  campaign,
  raffleKey,
  soldPercent,
  soldTickets,
  totalTickets,
  config,
  isLoadingConfig,
  lotteryContest,
  lotteryCombination,
  randomDate,
  minRandomDate,
  nextLotteryContest,
  maxLotteryContest,
  canScheduleBySoldPercent,
  lotterySourceIsTrusted,
  lotteryContestIsValid,
  canScheduleLottery,
  canScheduleRandom,
  randomDateIsValid,
  randomMinuteIsValid,
  activeMethod,
  lotteryMethodIsCompatible,
  randomMethodIsCompatible,
  onMethodChange,
  onLotteryContestChange,
  onLotteryCombinationChange,
  onRandomDateChange,
}: IProps) {
  const lotteryInfo = config?.lottery;
  const isLottery = activeMethod === "lottery";
  const isMethodAvailable = isLottery ? canScheduleLottery : canScheduleRandom;
  const methodTitle = isLottery ? "Loteria Federal" : "Sorteio Aleatório";

  return (
    <div className="d-flex flex-column gap-3">
      <div className="row g-3">
        <div className="col-lg-4">
          <div className="border rounded-3 p-3 h-100 bg-light bg-opacity-50">
            <div className="d-flex align-items-center gap-2 mb-3">
              <FaTicket className="text-primary fs-5" />
              <strong className="fs-5">Campanha</strong>
            </div>
            <div className="text-muted small">Identificação</div>
            <div className="fw-semibold" title="Identificação da campanha.">
              {campaign || "Campanha atual"}
            </div>
            <div className="text-muted small mt-2">Chave</div>
            <div className="fw-semibold" title="Chave criptografada da campanha.">
              {raffleKey || "Não informada"}
            </div>
          </div>
        </div>

        <div className="col-lg-4">
          <div className="border rounded-3 p-3 h-100 bg-light bg-opacity-50">
            <div className="d-flex align-items-center gap-2 mb-3">
              <FaTrophy className="text-warning fs-5" />
              <strong className="fs-5">Elegibilidade</strong>
            </div>
            <div
              className="fs-2 fw-bold"
              title="Percentual de tickets vendidos na campanha."
            >
              {soldPercent}%
            </div>
            <div className="text-muted">
              {masks.formatMilhar(soldTickets)} de {masks.formatMilhar(totalTickets || 0)} tickets vendidos
            </div>
            <div className="progress mt-3" style={{ height: 10 }}>
              <div
                className={`progress-bar bg-${
                  canScheduleBySoldPercent ? "success" : "warning"
                }`}
                style={{ width: `${Math.min(soldPercent, 100)}%` }}
              />
            </div>
          </div>
        </div>

        <div className="col-lg-4">
          <div className="border rounded-3 p-3 h-100 bg-light bg-opacity-50">
            <div className="d-flex align-items-center gap-2 mb-3">
              <FaClock className="text-primary fs-5" />
              <strong className="fs-5">Configuração</strong>
            </div>
            {isLoadingConfig || !config ? (
              <span className="text-muted">Carregando configuração...</span>
            ) : (
              <div className="d-flex flex-column gap-1">
                <div className="d-flex justify-content-between gap-3"><span className="text-muted">Mínimo vendido</span><strong>{config.minPercentSold}%</strong></div>
                {isLottery ? (
                  <>
                    <div className="d-flex justify-content-between gap-3"><span className="text-muted">Próximo concurso</span><strong>{config.lottery?.nextLotteryContest || "Indisponível"}</strong></div>
                    <div className="d-flex justify-content-between gap-3"><span className="text-muted">Último resultado</span><strong>{config.lottery?.lastContest || "Indisponível"}</strong></div>
                  </>
                ) : (
                  <>
                    <div className="d-flex justify-content-between gap-3"><span className="text-muted">Intervalo aleatório</span><strong>{config.intervalMinTime} min</strong></div>
                    <div className="d-flex justify-content-between gap-3"><span className="text-muted">Antecedência mínima</span><strong>{config.minMinutesToResult} min</strong></div>
                  </>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="row g-2">
        <div className="col-md-6">
          <MethodTab
            active={isLottery}
            icon={FaLandmark}
            title="Loteria Federal"
            description="Usa o resultado oficial de um concurso como referência."
            color="primary"
            isCompatible={lotteryMethodIsCompatible}
            onClick={() => onMethodChange("lottery")}
          />
        </div>
        <div className="col-md-6">
          <MethodTab
            active={!isLottery}
            icon={FaDice}
            title="Sorteio Aleatório"
            description="Defina uma data e o sistema realiza o sorteio automaticamente."
            color="success"
            isCompatible={randomMethodIsCompatible}
            onClick={() => onMethodChange("random")}
          />
        </div>
      </div>

      <div className="row g-3">
        <div className="col-lg-8">
          <section className="border rounded-3 p-4 h-100">
            <div className="d-flex justify-content-between align-items-start gap-3 mb-4">
              <div className="d-flex align-items-start gap-3">
                {isLottery ? <FaLandmark className="fs-2 text-primary mt-1" /> : <FaDice className="fs-2 text-success mt-1" />}
                <div>
                  <h6 className="fw-bold fs-5 mb-1">{methodTitle}</h6>
                  <p className="text-muted mb-0">
                    {isLottery
                      ? "Usa o resultado oficial de um concurso da Loteria Federal como referência para definir o ticket vencedor."
                      : "Defina uma data fixa e sorteie aleatoriamente apenas entre os tickets comprados da campanha."}
                  </p>
                </div>
              </div>
              <StatusPill
                can={isMethodAvailable}
                text={isMethodAvailable ? "Pode agendar" : "Pendente"}
                title="Status calculado pelas regras de configuração do sorteio."
              />
            </div>

            <h6 className="fw-bold mb-3">Regras e validações</h6>

            {isLottery ? (
              <>
                <RuleRow
                  can={lotterySourceIsTrusted}
                  icon={FaLandmark}
                  label="Fonte da Loteria Federal"
                  value={lotterySourceIsTrusted ? `Fonte ${lotteryInfo?.source || "não informada"} validada. Último concurso: ${lotteryInfo?.lastContest || "não informado"} em ${getLotteryLastDrawDateText(lotteryInfo?.lastDrawDate)}.` : getRaffleDefaultDrawLotteryReason(lotteryInfo?.reason).text}
                  title="Valida se o próximo concurso da Loteria Federal está confiável para agendamento."
                />
                <RuleRow
                  can={canScheduleBySoldPercent}
                  icon={FaChartPie}
                  label="Percentual mínimo vendido"
                  value={`Necessário ${config?.minPercentSold || 0}% vendido. Atual: ${soldPercent}%.`}
                  title="Valida se a campanha atingiu o percentual mínimo de tickets vendidos."
                />
                <RuleRow
                  can={!!config?.allowFutureContestScheduling}
                  icon={FaCalendarCheck}
                  label="Concursos futuros"
                  value={config?.allowFutureContestScheduling ? `Permite até ${config.maxFutureContestsAhead} concursos à frente.` : "Permite somente o próximo concurso."}
                  title="Valida se o backend permite agendar concursos além do próximo."
                />

                <div className="row g-3 mt-1">
                  <div className="col-md-6">
                    <Input label="Concurso" type="number" min={nextLotteryContest} max={maxLotteryContest} value={lotteryContest} disabled={!lotterySourceIsTrusted} onChange={(event) => onLotteryContestChange(event.target.value)} title="Número do concurso da Loteria Federal." />
                  </div>
                  <div className="col-md-6">
                    <Input label="Combinação" type="number" min={1} max={5} value={lotteryCombination} disabled={!lotterySourceIsTrusted} onChange={(event) => onLotteryCombinationChange(event.target.value)} title="Combinação da Loteria Federal usada como referência." />
                  </div>
                  <div className="col-12">
                    <div className="rounded-3 p-3 bg-light">
                      <small className="text-muted">Faixa aceita</small>
                      <div className="fw-semibold">{lotterySourceIsTrusted ? `${nextLotteryContest} até ${maxLotteryContest}` : "Indisponível"}</div>
                    </div>
                  </div>
                </div>

                {!lotterySourceIsTrusted && (
                  <div className="alert alert-warning mt-3 mb-0">Resultado da Loteria Federal indisponível para agendamento no momento.</div>
                )}
                {lotterySourceIsTrusted && !lotteryContestIsValid && (
                  <div className="alert alert-warning mt-3 mb-0">Informe um concurso dentro da faixa permitida.</div>
                )}
              </>
            ) : (
              <>
                <RuleRow can={canScheduleBySoldPercent} icon={FaChartPie} label="Percentual mínimo vendido" value={`Necessário ${config?.minPercentSold || 0}% vendido. Atual: ${soldPercent}%.`} title="Valida se a campanha atingiu o percentual mínimo de tickets vendidos." />
                <RuleRow can={randomDateIsValid} icon={FaClock} label="Antecedência mínima" value={`Agende com pelo menos ${config?.minMinutesToResult || 0} minutos de antecedência.`} title="Valida se a data escolhida respeita o tempo mínimo até o resultado." />
                <RuleRow can={randomMinuteIsValid} icon={FaCalendarCheck} label="Intervalo de horário" value={`Horários aceitos de ${config?.intervalMinTime || 0} em ${config?.intervalMinTime || 0} minutos.`} title="Valida se o minuto escolhido respeita o intervalo configurado." />

                <div className="row g-3 mt-1">
                  <div className="col-md-7">
                    <Input label="Data do sorteio" type="datetime-local" value={randomDate} min={getDateTimeLocalValue(minRandomDate)} onChange={(event) => onRandomDateChange(event.target.value)} title="Data e horário do sorteio aleatório." />
                  </div>
                  <div className="col-md-5">
                    <div className="rounded-3 p-3 h-100 bg-light">
                      <small className="text-muted">Mínimo permitido</small>
                      <div className="fw-semibold">{minRandomDate.format("DD/MM/YYYY HH:mm")}</div>
                    </div>
                  </div>
                </div>

                {randomDate && (!randomDateIsValid || !randomMinuteIsValid) && (
                  <div className="alert alert-warning mt-3 mb-0">Ajuste a data para respeitar a antecedência mínima e o intervalo configurado.</div>
                )}
              </>
            )}
          </section>
        </div>
        <div className="col-lg-4">
          <MethodInfo method={activeMethod} />
        </div>
      </div>
    </div>
  );
}
